import type { ReactNode } from "react";
import { createContext, useContext, useLayoutEffect, useState } from "react";

const OverlayContainer = createContext<HTMLElement | undefined>(undefined);
const ModalActive = createContext(true);
const ModalContainer = createContext<HTMLElement | undefined>(undefined);
export function useOverlayContainer() {
  return useContext(OverlayContainer);
}

/** Retain the requested dialog state, but release modality while its app is in the background. */
export function useScopedModal(open: boolean | undefined, defaultOpen: boolean | undefined) {
  const active = useContext(ModalActive);
  const [localOpen, setOpen] = useState(defaultOpen ?? false);
  return { open: active && (open ?? localOpen), setOpen };
}

/** Blocking dialogs sit above the desktop; their nested menus must use that same layer. */
export function ModalOverlayScope({ children }: { children: ReactNode }) {
  return (
    <OverlayContainer.Provider value={useContext(ModalContainer)}>
      {children}
    </OverlayContainer.Provider>
  );
}

/**
 * Portals belong to their app even while React Activity retains its hidden tree.
 *
 * `parent` and `zIndex` place the container beside its own window so a background window's menu
 * cannot paint over a window stacked above it. Without them the container sits on the body and
 * always wins, because the workspace frame isolates its own stacking context.
 */
export function OverlayScope({
  active,
  modalActive = active,
  parent,
  modalParent,
  zIndex,
  children,
}: {
  active: boolean;
  modalActive?: boolean;
  parent?: HTMLElement | null;
  modalParent?: HTMLElement | null;
  zIndex?: number;
  children: ReactNode;
}) {
  const [container] = useState(() => document.createElement("div"));
  const [modalContainer] = useState(() => document.createElement("div"));
  useLayoutEffect(() => {
    (modalParent ?? document.body).appendChild(modalContainer);
    return () => modalContainer.remove();
  }, [modalContainer, modalParent]);
  useLayoutEffect(() => {
    const host = parent ?? document.body;
    host.appendChild(container);
    return () => container.remove();
  }, [container, parent]);
  useLayoutEffect(() => {
    container.hidden = !active;
    container.inert = !active;
    modalContainer.hidden = !active;
    modalContainer.inert = !active;
    if (zIndex === undefined) {
      container.style.removeProperty("z-index");
      container.style.removeProperty("position");
      container.style.removeProperty("top");
      container.style.removeProperty("left");
      return;
    }
    container.style.position = "absolute";
    container.style.top = "0";
    container.style.left = "0";
    container.style.zIndex = String(zIndex);
  }, [active, container, modalContainer, zIndex]);
  return (
    <ModalActive.Provider value={modalActive}>
      <ModalContainer.Provider value={modalContainer}>
        <OverlayContainer.Provider value={container}>{children}</OverlayContainer.Provider>
      </ModalContainer.Provider>
    </ModalActive.Provider>
  );
}
